/*
 * Web sign-in hand-off (Helm XR web windows, Erwin 2026-10-28).
 *
 * Helm XR opens the admin console in an embedded browser at …#nx_handoff=<ticket>. The ticket
 * is a one-time, 60 s, origin-bound credential from POST /auth/web-handoff on Nexus; a URL
 * fragment never reaches a server. At boot, before routing and the auth check, we take it out
 * of the address bar (history.replaceState, other fragment parts kept), redeem it at
 * POST /auth/web-handoff/redeem and store the session with setToken / setRefreshToken. Any
 * failure falls through quietly to the normal sign-in page. The ticket is never logged.
 *
 * The redeemed session is never admin by itself (Erwin D2 2026-10-29): platform-admin power
 * needs the passkey step-up in this window — and an admin with no passkey is told to enrol one
 * (App.tsx EnrolPasskeyCard) — whatever auth.admin_mfa says.
 *
 * Login CSRF: a ticket never silently replaces a session of ANOTHER user already signed in in
 * this tab (someone could send a link carrying a ticket for their own account); it is dropped.
 *
 * S1 (Erwin 2026-10-29): the ticket is PKCE-bound to the glasses that asked for it. After taking it
 * from the fragment we ask the glasses' content script for the code verifier over a same-document
 * DOM event (lib/glasses.ts, <= 2.5 s) and redeem {ticket, codeVerifier}. Without a verifier (an
 * ordinary browser, a forwarded link) we still POST {ticket} — the gateway burns it with
 * VERIFIER_REQUIRED — and fall back quietly to the normal sign-in. Neither is ever logged.
 * Mirrors Helm web Web/src/lib/handoff.ts (tested there: Web/tests/handoff.test.ts).
 */

import { GATEWAY_BASE } from './config';
import { getToken, setRefreshToken, setToken } from './apiClient';
import { requestVerifier } from './glasses';
import type { BridgeTarget } from './glasses';

export const HANDOFF_PARAM = 'nx_handoff';

interface LocationLike { pathname: string; search: string; hash: string }
interface HistoryLike { state: unknown; replaceState(data: unknown, unused: string, url?: string | null): void }

/** Remove `nx_handoff=…` from the fragment (keeping the rest) and return the ticket, or null. */
export function takeHandoffTicket(loc: LocationLike, hist: HistoryLike): string | null {
  const raw = loc.hash.startsWith('#') ? loc.hash.slice(1) : loc.hash;
  if (!raw.includes(`${HANDOFF_PARAM}=`)) return null;
  let ticket: string | null = null;
  const keep: string[] = [];
  for (const part of raw.split('&')) {
    if (part.startsWith(`${HANDOFF_PARAM}=`)) {
      if (ticket === null) {
        try { ticket = decodeURIComponent(part.slice(HANDOFF_PARAM.length + 1)); } catch { ticket = ''; }
      }
    } else if (part) keep.push(part);
  }
  try {
    hist.replaceState(hist.state, '', `${loc.pathname}${loc.search}${keep.length ? `#${keep.join('&')}` : ''}`);
  } catch { /* best effort */ }
  return ticket ? ticket : null;
}

/** The claims of a JWT, decoded WITHOUT verifying it (only to read our own tokens). */
export function jwtClaims(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const v: unknown = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** The `sub` of a JWT, without verifying it (only to compare two of our own tokens). */
export function jwtSubject(token: string | null): string | null {
  const sub = jwtClaims(token)?.sub;
  return typeof sub === 'string' ? sub : null;
}

/** Was this access token minted for a Helm XR web hand-off session (amr contains `web_handoff`)? */
export function isHandoffSession(token: string | null): boolean {
  const amr = jwtClaims(token)?.amr;
  return Array.isArray(amr) ? amr.includes('web_handoff') : amr === 'web_handoff';
}

/** Does this access token carry a step-up (amr `mfa`, or `handoff_headset` for O2)? */
export function hasStepUpAmr(token: string | null): boolean {
  const amr = jwtClaims(token)?.amr;
  const list = Array.isArray(amr) ? amr : [amr];
  return list.includes('mfa') || list.includes('handoff_headset');
}

/**
 * Redeem a ticket for a session. True when signed in; false on any failure (never throws).
 * `codeVerifier` is the S1 PKCE verifier from the glasses; without it the gateway refuses (and
 * burns) the ticket.
 */
export async function redeemHandoff(ticket: string, codeVerifier: string | null = null): Promise<boolean> {
  try {
    const res = await fetch(`${GATEWAY_BASE}/auth/web-handoff/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(codeVerifier ? { ticket, codeVerifier } : { ticket }),
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { accessToken?: unknown; refreshToken?: unknown };
    if (typeof body.accessToken !== 'string' || !body.accessToken) return false;
    const current = jwtSubject(getToken());
    if (current && current !== jwtSubject(body.accessToken)) {
      // Keep the user already signed in; end the session the ticket just made (best effort).
      if (typeof body.refreshToken === 'string') {
        void fetch(`${GATEWAY_BASE}/auth/logout`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refreshToken: body.refreshToken }),
        }).catch(() => undefined);
      }
      return false;
    }
    setToken(body.accessToken);
    setRefreshToken(typeof body.refreshToken === 'string' ? body.refreshToken : null);
    return true;
  } catch {
    return false;
  }
}

/** Boot hook: strip and redeem a hand-off ticket (with the S1 verifier) if the URL carries one. */
export async function consumeHandoffAtBoot(
  loc: LocationLike = window.location,
  hist: HistoryLike = window.history,
  doc: BridgeTarget | null = typeof document === 'undefined' ? null : document,
  verifierTimeoutMs?: number,
): Promise<boolean> {
  const ticket = takeHandoffTicket(loc, hist);
  if (!ticket) return false;
  const codeVerifier = await requestVerifier(doc, verifierTimeoutMs);
  return redeemHandoff(ticket, codeVerifier);
}
